import { useState } from 'react'
import { Plus, Pencil, Trash2, Package, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useBuilds, useCreateBuild, useUpdateBuild, useDeleteBuild } from '../api/useBuilds'
import { useTestPlans } from '../api/useTestPlans'
import type { Build, CreateBuildRequest, UpdateBuildRequest } from '@/types/testplan.types'
import { logger } from '@/lib/utils/logger'
import { formatDate } from '@/lib/utils'

export default function BuildsPage() {
  const { data: builds, isLoading, error } = useBuilds()
  const { data: testplans } = useTestPlans()
  const createMutation = useCreateBuild()
  const updateMutation = useUpdateBuild()
  const deleteMutation = useDeleteBuild()

  const [filterPlanId, setFilterPlanId] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingBuild, setEditingBuild] = useState<Build | null>(null)
  const [formName, setFormName] = useState('')
  const [formPlanId, setFormPlanId] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [formReleaseDate, setFormReleaseDate] = useState('')
  const [formIsOpen, setFormIsOpen] = useState(true)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [buildToDelete, setBuildToDelete] = useState<{ id: number; name: string } | null>(null)

  const filteredBuilds = (builds || []).filter((b) => {
    if (filterPlanId !== 'all' && b.testplan_id.toString() !== filterPlanId) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return b.name.toLowerCase().includes(q) || b.notes?.toLowerCase().includes(q)
    }
    return true
  })

  const handleCreate = () => {
    setDialogMode('create')
    setEditingBuild(null)
    setFormName('')
    setFormPlanId('')
    setFormNotes('')
    setFormReleaseDate('')
    setFormIsOpen(true)
    setDialogOpen(true)
  }

  const handleEdit = (build: Build) => {
    setDialogMode('edit')
    setEditingBuild(build)
    setFormName(build.name)
    setFormPlanId(build.testplan_id.toString())
    setFormNotes(build.notes || '')
    setFormReleaseDate(build.release_date || '')
    setFormIsOpen(build.is_open)
    setDialogOpen(true)
  }

  // openapi: POST /api/builds → 201
  const handleSubmit = async () => {
    if (!formName.trim()) { toast.error('Build name is required'); return }
    try {
      if (dialogMode === 'create') {
        if (!formPlanId) { toast.error('Select a test plan'); return }
        const req: CreateBuildRequest = {
          testplan_id: parseInt(formPlanId, 10),
          name: formName.trim(),
          notes: formNotes.trim() || undefined,
          release_date: formReleaseDate || undefined,
          is_open: formIsOpen,
        }
        await createMutation.mutateAsync(req)
        toast.success(`Build "${formName}" created`)
      } else if (editingBuild) {
        // openapi: PUT /api/builds/{build_id}
        const req: UpdateBuildRequest = {
          name: formName.trim(),
          notes: formNotes.trim() || undefined,
          release_date: formReleaseDate || undefined,
          is_open: formIsOpen,
        }
        await updateMutation.mutateAsync({ id: editingBuild.id, data: req })
        toast.success(`Build "${formName}" updated`)
      }
      setDialogOpen(false)
    } catch (err) {
      logger.error('Build save failed', { error: err })
      toast.error('Failed to save build')
    }
  }

  const handleDeleteClick = (id: number, name: string) => {
    setBuildToDelete({ id, name })
    setDeleteConfirmOpen(true)
  }

  // openapi: DELETE /api/builds/{build_id} → 204
  const handleDeleteConfirm = async () => {
    if (!buildToDelete) return
    try {
      await deleteMutation.mutateAsync(buildToDelete.id)
      toast.success(`"${buildToDelete.name}" deleted`)
    } catch (err) {
      logger.error('Delete build failed', { error: err })
      toast.error('Failed to delete build')
    } finally {
      setDeleteConfirmOpen(false)
      setBuildToDelete(null)
    }
  }

  if (error) {
    return <Card><CardContent className="pt-6"><p className="text-destructive">{error instanceof Error ? error.message : 'Failed to load builds'}</p></CardContent></Card>
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Builds</CardTitle>
              <CardDescription>{filteredBuilds.length} build{filteredBuilds.length !== 1 ? 's' : ''}</CardDescription>
            </div>
            <Button onClick={handleCreate}><Plus className="mr-2 h-4 w-4" /> New Build</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Select value={filterPlanId} onValueChange={setFilterPlanId}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Test Plans" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Test Plans</SelectItem>
                {testplans?.map((tp) => (
                  <SelectItem key={tp.id} value={tp.id.toString()}>{tp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Search builds..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-sm" />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : filteredBuilds.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No builds found.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Test Plan</TableHead>
                    <TableHead>Release Date</TableHead>
                    <TableHead>Executions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBuilds.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                          {b.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{b.testplan_name || `Plan #${b.testplan_id}`}</TableCell>
                      <TableCell className="text-sm">{b.release_date ? formatDate(b.release_date) : '—'}</TableCell>
                      <TableCell>
                        {b.execution_stats ? (
                          <div className="text-sm">
                            <span className="text-green-600">{b.execution_stats.passed}P</span>{' / '}
                            <span className="text-red-600">{b.execution_stats.failed}F</span>{' / '}
                            <span>{b.execution_stats.total}T</span>
                          </div>
                        ) : (b.execution_count ?? 0)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={b.active ? (b.is_open ? 'success' : 'default') : 'destructive'}>
                          {b.active ? (b.is_open ? 'Open' : 'Closed') : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(b)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(b.id, b.name)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Create Build' : 'Edit Build'}</DialogTitle>
            <DialogDescription>{dialogMode === 'create' ? 'Add a new build to a test plan.' : 'Update build details.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Build name (e.g., v2.1.0)" />
            </div>
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label>Test Plan</Label>
                <Select value={formPlanId} onValueChange={setFormPlanId}>
                  <SelectTrigger><SelectValue placeholder="Select test plan" /></SelectTrigger>
                  <SelectContent>
                    {testplans?.map((tp) => (
                      <SelectItem key={tp.id} value={tp.id.toString()}>{tp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Release Date</Label>
              <Input type="date" value={formReleaseDate} onChange={(e) => setFormReleaseDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={3} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="build-open" checked={formIsOpen} onCheckedChange={(c) => setFormIsOpen(!!c)} />
              <Label htmlFor="build-open">Open (accepting executions)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Build"
        description={`Are you sure you want to delete "${buildToDelete?.name}"? Associated executions will be affected.`}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  )
}
